/**
 * ドメイン詳細 / DNS 設定手順（05-ui.md §8.2）— **契約者パネルで最も重要な画面**。
 *
 * ここでの失敗が「メールが届かない」という最悪の問い合わせに直結するので、
 * 次の 4 点を必ず守る:
 *   1. 表示する期待値は API（= サーバー側の `buildExpectedDnsRecords()`）が返したものだけ。
 *      画面で組み立てると検証ジョブと食い違い、正しく設定しても永久に verified にならない。
 *   2. 値は必ずコピーで渡す（手入力させない）。
 *   3. 反映に時間がかかることを、成功・失敗どちらの状態でも明記する。
 *   4. 「今すぐ再チェック」は 202（受理のみ）。完了までポーリングして結果を反映する。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CopyableCode,
  DnsRecordTable,
  Field,
  KeyValue,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mail-sv/ui';
import { ArrowLeft, ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useDomains } from '../lib/hooks.js';
import { AppLink, useAppNavigate, useCurrentPath, useRouteParams } from '../lib/nav.js';
import { Async, ErrorAlert, LoadingBlock } from '../components/async.js';
import type { Tone } from '@mail-sv/shared';
import { Disclosure, HelpNote, StepList } from '../components/help.js';
import { FormAlert } from '../components/form.js';
import { ConfirmTextDialog } from '../components/confirm-text-dialog.js';
import { DNS_PROVIDERS, findProvider, loadPreferredProvider, savePreferredProvider } from '../lib/dns-providers.js';
import { formatAgo } from '../lib/format.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import type {
  CatchAll,
  CertificateState,
  CursorPage,
  DmarcState,
  DomainSummary,
  Mailbox,
} from '../lib/api-types.js';
import { toDnsRecordsView, type DnsRecordsApiResponse, type DnsRecordsView } from '../lib/dns-records.js';

/** 種別ごとの平易な説明（05-ui.md §8.2 の表をそのまま使う）。 */
const KIND_HELP: Record<string, string> = {
  ownership_txt:
    'このドメインがお客様のものであることを確認するための、一時的な目印です。確認が終われば削除しても構いません。',
  mx: 'このドメイン宛のメールを、どのサーバーが受け取るかを指定します。これが無いとメールが届きません。',
  spf: '「このドメインからのメールは、このサーバーが送っています」と宣言する設定です。無いと迷惑メール扱いされやすくなります。',
  dkim: '送信するメールに電子署名を付けて、なりすましを防ぐ設定です。',
  dmarc: 'SPF / DKIM に失敗したメールをどう扱うかを、受信側に指示する設定です。',
  autoconfig:
    'Outlook や Thunderbird がサーバー設定を自動取得するための設定です。任意ですが、設定するとお客様の手間が減ります。',
  autodiscover:
    'Outlook がサーバー設定を自動取得するための設定です。任意ですが、設定するとお客様の手間が減ります。',
};

export function DomainDetailPage() {
  const params = useRouteParams();
  const path = useCurrentPath();
  const domainId = params.domainId ?? '';
  const domains = useDomains();
  const { user } = useAuth();
  const navigate = useAppNavigate();

  const domain = (domains.data?.items ?? []).find((d) => d.id === domainId);
  /*
   * `/domains/$id/certificate` から来たときは証明書タブ。
   * **タブを URL で名指しできるようにしてある理由**は、メールソフト設定の
   * 画面から「このドメインの証明書は使えるようになったか」へ直接飛ばしたいから。
   * 飛び先が DNS タブだと、契約者は自分でタブを探すことになる。
   * それ以外の初期表示は DNS（このドメインでまずやることが DNS 設定だから）。
   */
  const defaultTab = path.endsWith('/certificate') ? 'certificate' : 'dns';

  return (
    <>
      <PageHeader
        title={domain?.name ?? 'ドメイン'}
        description="このドメインの DNS 設定・受け取り方の設定を行います。"
        breadcrumbs={[{ label: 'ドメイン', href: '/domains' }, { label: domain?.name ?? '' }]}
        actions={
          <Button asChild variant="outline" size="sm">
            <AppLink to="/domains">
              <ArrowLeft aria-hidden />
              一覧に戻る
            </AppLink>
          </Button>
        }
      />

      {domains.isPending ? (
        <LoadingBlock lines={5} />
      ) : !domain ? (
        <Alert variant="destructive">
          <AlertTitle>ドメインが見つかりません</AlertTitle>
          <AlertDescription>
            削除されたか、URL が誤っている可能性があります。
            <Button asChild variant="outline" size="sm" className="ml-2">
              <AppLink to="/domains">一覧へ</AppLink>
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <Tabs defaultValue={defaultTab}>
          <TabsList>
            <TabsTrigger value="dns">DNS 設定</TabsTrigger>
            <TabsTrigger value="certificate">TLS 証明書</TabsTrigger>
            <TabsTrigger value="catch-all">キャッチオール</TabsTrigger>
            {canManageOrg(user) ? <TabsTrigger value="danger">削除</TabsTrigger> : null}
          </TabsList>

          <TabsContent value="dns">
            <DnsSection domainId={domainId} domainName={domain.name} />
            <div className="mt-6">
              <DmarcGuide domain={domain} />
            </div>
          </TabsContent>

          <TabsContent value="certificate">
            <CertificateSection domainId={domainId} domainName={domain.name} />
          </TabsContent>

          <TabsContent value="catch-all">
            <CatchAllSection domainId={domainId} domainName={domain.name} />
          </TabsContent>

          {canManageOrg(user) ? (
            <TabsContent value="danger">
              <DangerSection
                domainId={domainId}
                domainName={domain.name}
                mailboxCount={domain.mailboxCount}
                onDeleted={() => navigate({ to: '/domains', replace: true })}
              />
            </TabsContent>
          ) : null}
        </Tabs>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ DMARC */

/** 段階の並び。**飛ばして `reject` へ行かせない**ことがこの画面の目的。 */
const DMARC_STAGES = [
  {
    policy: 'none' as const,
    title: '第 1 段階: 監視だけ（p=none）',
    detail:
      'まずは何も止めずに、レポートだけ受け取ります。自社のメールがどこから送られているかを把握する段階です。最低 2 週間は様子を見てください。',
  },
  {
    policy: 'quarantine' as const,
    title: '第 2 段階: 迷惑メール扱い（p=quarantine）',
    detail:
      'なりすましと判定されたメールが受信側の迷惑メールフォルダへ入るようになります。レポートに自社の正規メールの失敗が出なくなってから進んでください。',
  },
  {
    policy: 'reject' as const,
    title: '第 3 段階: 受け取り拒否（p=reject）',
    detail:
      'なりすましメールは受信側で破棄されます。設定漏れがあると自社の正規メールも届かなくなるため、必ず第 2 段階で十分に確認してから進んでください。',
  },
];

/**
 * DMARC の段階的な引き上げ（R124）。
 *
 * **DMARC は「いきなり `p=reject` にして自社のメールが全部消える」事故が
 * 定番**なので、順序と待ち時間を画面で固定する。
 *
 * ## 以前ここは「案内だけ」で、それが実害を出していた
 *
 * 旧版のコメントは「`dmarcPolicy` は `GET /portal/domains/:id` が返すだけで
 * 書き換える API が無い」と書いていたが、**`PUT /domains/:id/dmarc` は
 * 実装済み**で、そちらを呼ばない限り `domains.dmarc_policy` は永久に
 * `none` のまま残る。その状態でこの画面の案内どおりに DNS を
 * `p=quarantine` へ書き換えると、
 *
 *   - 期待値は `buildDmarcValue(dmarcPolicy=none)` から作られる（`required: true`）
 *   - 検証は `p=none` の部分一致を要求する（`dns-verify.ts`）
 *
 * ので **DMARC レコードが不一致になり、`dnsStatus` が ok から落ちる**。
 * つまり「案内どおりにやると DNS 検証が壊れる」画面だった。
 *
 * だから引き上げは必ず `PUT /domains/:id/dmarc` を先に通す。サーバが
 * ポリシーを保存し、期待値を作り直し、検証ジョブを積む（この 3 つが
 * 揃わないと画面と実態がずれる）。引き上げの可否・阻害理由は
 * `GET /domains/:id/dmarc`（`assessDmarc`）が機械可読で返すので、
 * **「できません」だけを出さずに、何が足りないのかをそのまま並べる。**
 */
function DmarcGuide({ domain }: { domain: DomainSummary }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canWrite = canManageOrg(user);
  const domainId = domain.id;

  const state = useQuery({
    queryKey: qk.dmarc(domainId),
    queryFn: () => api.get<DmarcState>(`/domains/${encodeURIComponent(domainId)}/dmarc`),
  });

  const advance = useMutation({
    mutationFn: (policy: 'none' | 'quarantine' | 'reject') =>
      api.put<unknown>(`/domains/${encodeURIComponent(domainId)}/dmarc`, { policy }),
    onSuccess: async () => {
      /*
       * **期待値（`_dmarc` TXT）はサーバ側で作り直される。** 3 つとも
       * 取り直さないと、画面に古い値が残ったまま「貼り付けてください」と
       * 案内することになる。
       */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.dmarc(domainId) }),
        queryClient.invalidateQueries({ queryKey: qk.domain(domainId) }),
        queryClient.invalidateQueries({ queryKey: qk.dnsRecords(domainId) }),
      ]);
    },
  });

  // 保存済みの値を正とする（`GET /domains/:id` と `GET .../dmarc` は同じ列）。
  const policy = state.data?.policy ?? domain.dmarcPolicy;
  const currentIndex = DMARC_STAGES.findIndex((s) => s.policy === policy);
  const rua = state.data?.rua ?? domain.dmarcRua;
  const next = currentIndex >= 0 && currentIndex < DMARC_STAGES.length - 1
    ? DMARC_STAGES[currentIndex + 1]
    : null;

  /** 貼り付ける TXT の値。rua が未設定なら項目ごと出さない（空の rua は不正）。 */
  const txtValue = (p: 'none' | 'quarantine' | 'reject'): string =>
    rua ? `v=DMARC1; p=${p}; rua=mailto:${rua}; adkim=r; aspf=r` : `v=DMARC1; p=${p}`;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">なりすまし対策（DMARC）を強くする</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          DMARC は「SPF や DKIM に失敗したメールをどう扱うか」を受信側に指示する設定です。
          <strong>いきなり強い設定にすると、自社の正規のメールまで届かなくなります。</strong>
          次の順番で、それぞれ 2 週間ほど様子を見ながら進めてください。
        </p>

        <ol className="space-y-3">
          {DMARC_STAGES.map((stage, i) => {
            const done = currentIndex >= 0 && i < currentIndex;
            const current = i === currentIndex;
            return (
              <li
                key={stage.policy}
                className={
                  current
                    ? 'rounded-lg border border-primary/50 bg-panel p-3'
                    : 'rounded-lg border border-border p-3'
                }
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{stage.title}</span>
                  {current ? (
                    <Badge size="sm">いまここ</Badge>
                  ) : done ? (
                    <Badge variant="muted" size="sm">
                      設定済み
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{stage.detail}</p>
              </li>
            );
          })}
        </ol>

        {next ? (
          <div className="space-y-3">
            <FormAlert error={advance.error} />

            {/*
             * **引き上げの阻害理由をそのまま並べる。** サーバ
             * （`assessDmarc()`）が「何が足りないか」を機械可読で返すのは、
             * 画面が「サポートに聞いてください」としか書けない状態を
             * 作らないため。ここで畳むとその設計が無意味になる。
             */}
            {state.data && state.data.blockers.length > 0 ? (
              <Alert variant="warning">
                <AlertTitle>まだ次の段階に進めません</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc space-y-1 pl-4">
                    {state.data.blockers.map((b) => (
                      <li key={b.code}>{b.message}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : null}

            <p className="text-sm font-medium">次に進む手順</p>
            <ol className="list-decimal space-y-2 pl-5 text-sm">
              <li>
                下のボタンで「次の段階に進む」を押します（このサービス側の設定が切り替わります）。
              </li>
              <li>
                切り替わった後に案内される新しい TXT の値を、お客様の DNS に設定します。
              </li>
              <li>反映後、上の「今すぐ再チェック」で確認します。</li>
            </ol>
            <Alert variant="info">
              <AlertTitle>順番を逆にしないでください</AlertTitle>
              <AlertDescription>
                先に DNS だけを書き換えると、このサービスが確認している値と食い違い、DNS
                の確認結果が「設定に問題があります」に変わります。必ずボタンを先に押してください。
              </AlertDescription>
            </Alert>

            {canWrite ? (
              <Button
                type="button"
                loading={advance.isPending}
                disabled={state.isPending || !(state.data?.canRaise ?? false)}
                onClick={() => advance.mutate(next.policy)}
              >
                {next.title.replace(/^第 \d+ 段階: /, '')}に進む
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                この操作は組織の管理者だけが行えます。
              </p>
            )}

            <p className="text-sm font-medium">現在の設定で必要な DNS の値</p>
            <CopyableCode label={`_dmarc.${domain.name}（TXT）`} value={txtValue(policy)} />
            <p className="text-xs text-muted-foreground">
              上のボタンを押すとこの値が更新されます。更新後の値をお客様の DNS に設定してください。
              （次の段階の値は <code className="font-mono">{txtValue(next.policy)}</code> になります）
            </p>
          </div>
        ) : (
          <Alert variant="success">
            <AlertTitle>いちばん強い設定になっています</AlertTitle>
            <AlertDescription>
              このドメインを騙るメールは、受信側で破棄されます。送信元を増やしたとき（外部の配信サービスなど）は、
              SPF と DKIM を先に整えてください。
            </AlertDescription>
          </Alert>
        )}

        {rua ? (
          <p className="text-xs text-muted-foreground">
            レポートの送付先: <span className="font-medium">{rua}</span>
            （受信側から集計レポートが届きます。中身の自動集計にはまだ対応していません）
          </p>
        ) : (
          <Alert variant="warning">
            <AlertTitle>レポートの送付先が設定されていません</AlertTitle>
            <AlertDescription>
              レポート（rua）を受け取らないまま設定を強めると、どのメールが弾かれているのか分からないまま進むことになります。
              先に受け取り用のアドレスをご用意ください。
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------- TLS 証明書 */

/**
 * 証明書の状態 → 画面の見え方。`not_requested` は API 側が合成する値
 * （行が無いのは異常ではないので 404 にしない、という判断の帰結）。
 *
 * 訳語とトーンの出典は `@mail-sv/shared` の `labelOf` / `toneOf` が原則だが、
 * `active` / `pending` / `failed` は他の文脈でも使われる汎用の語彙で、
 * 「証明書が有効」を表す訳語がそちらには無い。ここだけ上書きする。
 */
const CERT_STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: '有効', tone: 'success' },
  pending: { label: '取得中', tone: 'info' },
  failed: { label: '取得に失敗', tone: 'destructive' },
  revoked: { label: '失効', tone: 'destructive' },
  expired: { label: '期限切れ', tone: 'destructive' },
  not_requested: { label: '未発行', tone: 'muted' },
};

/**
 * 独自ホスト名の TLS 証明書（R125）。
 *
 * ACME 取得・SNI 配布・プランゲートは実装済みなのに、**契約者が
 * 「自分のホスト名で TLS が使えるようになったか」を知る手段が無かった。**
 * 状態が見えないと、メールソフトの接続先を共通ホスト名から切り替える判断が
 * できず、機能があっても使われない。
 *
 * **`lastError` はそのまま出す。** 失敗の原因は DNS の TXT が未反映など
 * 契約者自身にしか直せないものがほとんどで、隠すと自力で直せる失敗まで
 * サポート起票になる（API 側もその方針で返している）。
 */
function CertificateSection({ domainId, domainName }: { domainId: string; domainName: string }) {
  const cert = useQuery({
    queryKey: qk.certificate(domainId),
    queryFn: () =>
      api.get<CertificateState>(`/domains/${encodeURIComponent(domainId)}/certificate`),
  });

  return (
    <div className="space-y-4">
      <HelpNote title="独自のホスト名でメールソフトを設定するための証明書">
        メールソフトの接続先を <code className="font-mono">mail.{domainName}</code>{' '}
        のようなお客様のホスト名にするには、そのホスト名の TLS 証明書が必要です。
        取得と更新はこちらで自動的に行います。「有効」になるまでは、共通のホスト名をお使いください。
      </HelpNote>

      <Async query={cert} lines={4}>
        {(data) => {
          const view = CERT_STATUS[data.status] ?? { label: data.status, tone: 'default' as const };
          return (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  証明書の状態
                  <StatusBadge
                    status={data.status}
                    tone={view.tone}
                    label={view.label}
                    size="sm"
                    dot
                  />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.status === 'not_requested' ? (
                  <Alert variant="info">
                    <AlertTitle>まだ発行されていません</AlertTitle>
                    <AlertDescription>
                      このドメインでは独自のホスト名を有効にしていないため、証明書は発行されていません。
                      メールソフトには
                      <AppLink
                        to="/mail-clients"
                        className="mx-1 text-primary underline-offset-4 hover:underline"
                      >
                        共通のホスト名
                      </AppLink>
                      をご設定ください。
                    </AlertDescription>
                  </Alert>
                ) : null}

                {data.lastError ? (
                  <Alert variant="destructive">
                    <AlertTitle>証明書の取得に失敗しています</AlertTitle>
                    <AlertDescription className="space-y-1">
                      <p className="font-mono text-xs">{data.lastError}</p>
                      <p>
                        DNS の設定が反映されていない場合、反映後に自動で再取得します。
                        {data.failureCount && data.failureCount > 3
                          ? '繰り返し失敗しているため、DNS 設定をご確認ください。'
                          : null}
                      </p>
                    </AlertDescription>
                  </Alert>
                ) : null}

                {data.status === 'active' ? (
                  <Alert variant="success">
                    <AlertTitle>このホスト名で接続できます</AlertTitle>
                    <AlertDescription>
                      メールソフトの接続先を{' '}
                      <span className="font-mono">{data.commonName ?? `mail.${domainName}`}</span>{' '}
                      に切り替えていただけます。更新は自動で行います。
                    </AlertDescription>
                  </Alert>
                ) : null}

                <KeyValue
                  columns={2}
                  items={[
                    /*
                     * **`hostname` ではなく `commonName` を出す。** API の
                     * `hostname` は `domains.mail_hostname`（＝ノード側の受信
                     * ホスト名。既定は共通の `mx1.<サービスドメイン>`）で、
                     * 証明書が発行される名前ではない。証明書を作る
                     * `ensureDomainCertificate()` は `mail.<ドメイン名>` で
                     * 発行するので、そちらを出さないと「共通ホスト名を
                     * 独自ホスト名として案内する」ことになる。
                     * 未発行のときは `commonName` が返らないので組み立てる。
                     */
                    { key: 'ホスト名', value: data.commonName ?? `mail.${domainName}` },
                    {
                      key: '有効期限',
                      value: data.notAfter
                        ? `${new Date(data.notAfter).toLocaleDateString('ja-JP')}${
                            data.daysRemaining !== null ? `（あと ${data.daysRemaining} 日）` : ''
                          }`
                        : '—',
                    },
                    { key: '発行者', value: data.issuer ?? '—' },
                    {
                      key: '確認の方法',
                      value: data.challengeType ? data.challengeType.toUpperCase() : '—',
                    },
                  ]}
                />

                {data.san && data.san.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      対象のホスト名
                    </p>
                    <ul className="space-y-0.5">
                      {data.san.map((s) => (
                        <li key={s} className="font-mono text-xs">
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <Button asChild variant="outline" size="sm">
                  <AppLink to="/mail-clients">メールソフトの設定を見る</AppLink>
                </Button>
              </CardContent>
            </Card>
          );
        }}
      </Async>
    </div>
  );
}

/* ------------------------------------------------------------------ DNS 設定 */

function DnsSection({ domainId, domainName }: { domainId: string; domainName: string }) {
  const queryClient = useQueryClient();
  const [providerId, setProviderId] = React.useState(() => loadPreferredProvider());
  // 再チェックは 202 を返すだけでジョブ ID は返らない（DNS 伝播待ちのキュー投入）。
  // 完了は「レコードの判定が変わったか」でしか分からないので、受理してから
  // しばらくポーリングして拾う。
  const [verifying, setVerifying] = React.useState(false);

  const dns = useQuery({
    queryKey: qk.dnsRecords(domainId),
    // API は期待値と観測値を 1 レコードに同居させて返す。UI は 2 配列を前提に
    // 書かれているので、境界で `toDnsRecordsView()` に変換を閉じ込める。
    queryFn: async () =>
      toDnsRecordsView(
        await api.get<DnsRecordsApiResponse>(`/domains/${encodeURIComponent(domainId)}/dns-records`),
        domainName,
      ),
    // 検証は手動トリガ。実行中だけ 3 秒で追いかける（§5.3）。
    refetchInterval: verifying ? 3000 : false,
    staleTime: 0,
  });

  const verify = useMutation({
    mutationFn: () =>
      api.post<{ accepted: boolean; status: string }>(
        `/domains/${encodeURIComponent(domainId)}/verify`,
      ),
    onSuccess: () => setVerifying(true),
  });

  // 判定が `unchecked` を抜けたら追いかけをやめる。DNS の伝播待ちで
  // 何時間も回り続けないよう、上限（60 秒）でも打ち切る。
  React.useEffect(() => {
    if (!verifying) return undefined;
    const timer = setTimeout(() => {
      setVerifying(false);
      void queryClient.invalidateQueries({ queryKey: qk.domains() });
    }, 60_000);
    return () => clearTimeout(timer);
  }, [verifying, queryClient]);

  const provider = findProvider(providerId);

  return (
    <div className="space-y-6">
      <Async query={dns} lines={6}>
        {(data) => (
          <>
            <OverallBanner overall={data.overall} records={data} />

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">① どこでドメインを取得しましたか？</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                  <Field label="ドメイン管理会社" className="min-w-[16rem] flex-1">
                    <Select
                      value={providerId}
                      onValueChange={(v) => {
                        setProviderId(v);
                        savePreferredProvider(v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        {DNS_PROVIDERS.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  {provider.consoleUrl ? (
                    <Button asChild variant="outline">
                      <a href={provider.consoleUrl} target="_blank" rel="noreferrer noopener">
                        {provider.name} の管理画面を開く
                        <ExternalLink aria-hidden />
                      </a>
                    </Button>
                  ) : null}
                </div>

                <StepList steps={provider.steps} />

                {provider.caution ? (
                  <Alert variant="warning">
                    <AlertDescription>{provider.caution}</AlertDescription>
                  </Alert>
                ) : null}

                <p className="text-xs text-muted-foreground">
                  ※ 設定する値は、どの会社でドメインを取得していても同じです。
                </p>
              </CardContent>
            </Card>

            <Section
              title="② 次のレコードを追加してください"
              description="「コピー」を押して、そのまま貼り付けてください。1 文字でも違うと確認が通りません。"
            >
              <div className="overflow-x-auto">
                <DnsRecordTable
                  records={data.records}
                  results={data.checks}
                  showDescription={false}
                />
              </div>

              <div className="space-y-2">
                {data.records.map((r, i) => (
                  <Disclosure key={`${r.kind}-${i}`} summary={`${r.type} レコード（${r.kind}）ってなに？`}>
                    {KIND_HELP[r.kind] ?? r.description}
                  </Disclosure>
                ))}
              </div>

              <CopyableCode
                label="まとめてコピー（ゾーンファイル形式）"
                value={toZoneFile(data)}
              />
            </Section>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-panel px-4 py-3">
              <div className="text-sm text-muted-foreground">
                最終チェック: {formatAgo(data.checkedAt)}
                {verifying ? '（確認中…）' : ''}
              </div>
              <Button
                onClick={() => verify.mutate()}
                loading={verify.isPending || verifying}
              >
                <RefreshCw aria-hidden />
                今すぐ再チェック
              </Button>
            </div>

            {verify.isError ? <ErrorAlert error={verify.error} /> : null}

            <HelpNote title="設定したのに「未設定」と表示されるとき">
              DNS の変更が世界中に伝わるまで、最大 24 時間かかることがあります（多くの場合は 5〜30 分）。
              しばらく待ってから「今すぐ再チェック」をお試しください。
              1 日経っても変わらない場合は、入力した値が 1 文字違っていないかご確認ください。
            </HelpNote>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">メールソフトの接続先</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {domainName} のメールアドレスは、次のサーバーに接続して送受信します。
                </p>
                <CopyableCode label="メールサーバー（IMAP / SMTP 共通）" value={data.mailHostname} />
                <Button asChild variant="outline" size="sm">
                  <AppLink to="/mail-clients">詳しい設定値を見る</AppLink>
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </Async>
    </div>
  );
}

function OverallBanner({
  overall,
  records,
}: {
  overall: DnsRecordsView['overall'];
  records: DnsRecordsView;
}) {
  const remaining = records.checks.filter((c) => c.result !== 'ok').length;

  if (overall === 'verified') {
    return (
      <Alert variant="success">
        <AlertTitle>設定は完了しています</AlertTitle>
        <AlertDescription>
          このドメインでメールの送受信ができます。DNS の設定を変更すると再確認が必要になります。
        </AlertDescription>
      </Alert>
    );
  }
  if (overall === 'partial') {
    return (
      <Alert variant="warning">
        <AlertTitle>あと {remaining} 件の設定が必要です</AlertTitle>
        <AlertDescription>
          下の表で「未設定」「値が違う」と表示されている行を追加・修正してください。
          設定を反映してから実際に有効になるまで、最大 24 時間かかることがあります。
        </AlertDescription>
      </Alert>
    );
  }
  if (overall === 'unchecked') {
    return (
      <Alert variant="info">
        <AlertTitle>まだ確認していません</AlertTitle>
        <AlertDescription>
          下の手順で DNS を設定したあと、「今すぐ再チェック」を押してください。
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="destructive">
      <AlertTitle>DNS 設定が見つかりません</AlertTitle>
      <AlertDescription>
        このドメイン宛のメールはまだ届きません。下の手順に沿って設定してください。
        設定を反映してから有効になるまで、最大 24 時間かかることがあります。
      </AlertDescription>
    </Alert>
  );
}

/**
 * TXT の値をゾーンファイルの「文字列」へ直す。
 *
 * **1 つの文字列は 255 オクテットまで**（RFC 1035 の character-string）。
 * 超えたぶんは複数の文字列に割って並べる（受け取り側が連結する）。
 * DKIM の rsa2048 公開鍵は `v=DKIM1; k=rsa; p=…` で 400 字前後になるため、
 * **1 本の `"…"` で書くと BIND も主要な DNS 事業者も取り込みを拒否する。**
 * 以前はここで割っていなかったので、「まとめてコピー」で出したゾーンを
 * 貼っても DKIM の行だけが必ず落ち、契約者は理由の分からないまま
 * 署名が付かない状態になっていた（画面上はコピーが成功して見える）。
 *
 * **先に区切ってからエスケープする。** `\"` は 1 オクテットとして数えるので、
 * 順序を逆にすると区切り位置がずれ、エスケープが 2 つの文字列に割れて壊れる。
 */
export function toTxtStrings(value: string): string {
  const chunks: string[] = [];
  for (let i = 0; i < value.length; i += 255) chunks.push(value.slice(i, i + 255));
  if (chunks.length === 0) chunks.push('');
  return chunks.map((c) => `"${c.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`).join(' ');
}

/** ゾーンファイル形式でまとめて出す。DNS 事業者によってはこれを取り込める。 */
export function toZoneFile(data: DnsRecordsView): string {
  return data.records
    .map((r) => {
      const value = r.type === 'TXT' ? toTxtStrings(r.value) : r.value;
      const prio = r.priority !== null ? `${r.priority} ` : '';
      return `${r.name}.\t${r.ttl}\tIN\t${r.type}\t${prio}${value}`;
    })
    .join('\n');
}

/* ------------------------------------------------------------- キャッチオール */

function CatchAllSection({ domainId, domainName }: { domainId: string; domainName: string }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = React.useState<string>('');
  const [touched, setTouched] = React.useState(false);

  const catchAll = useQuery({
    queryKey: qk.catchAll(domainId),
    queryFn: () => api.get<CatchAll>(`/domains/${encodeURIComponent(domainId)}/catch-all`),
  });

  const mailboxes = useQuery({
    queryKey: qk.mailboxes({ domainId, forCatchAll: true }),
    queryFn: () =>
      api.get<CursorPage<Mailbox>>(
        `/mailboxes?domainId=${encodeURIComponent(domainId)}&limit=200`,
      ),
  });

  React.useEffect(() => {
    if (!touched && catchAll.data) setSelected(catchAll.data.mailboxId ?? '');
  }, [catchAll.data, touched]);

  const save = useMutation({
    // API のリクエストキーは `mailboxId`。202 + jobId が返る。
    mutationFn: (mailboxId: string | null) =>
      api.put<{ jobId: string }>(`/domains/${encodeURIComponent(domainId)}/catch-all`, {
        mailboxId,
      }),
    onSuccess: () => {
      setTouched(false);
      void queryClient.invalidateQueries({ queryKey: qk.catchAll(domainId) });
      void queryClient.invalidateQueries({ queryKey: qk.domains() });
    },
  });

  return (
    <div className="space-y-5">
      <Alert variant="warning">
        <AlertTitle>キャッチオールは既定で無効です</AlertTitle>
        <AlertDescription>
          キャッチオールを有効にすると、<strong>存在しないアドレス宛のメールもすべて</strong>
          指定したメールアドレスで受け取ります。便利な反面、宛先を推測して送られてくる迷惑メールが
          大量に届く原因になります。必要な場合のみ有効にしてください。
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{domainName} の宛先不明メールの扱い</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {catchAll.isPending || mailboxes.isPending ? (
            <LoadingBlock lines={2} />
          ) : (
            <>
              <Field
                label="受け取るメールアドレス"
                hint="「受け取らない」を選ぶと、存在しない宛先へのメールは送信者にエラーが返ります（推奨）。"
              >
                <Select
                  value={selected === '' ? 'none' : selected}
                  onValueChange={(v) => {
                    setTouched(true);
                    setSelected(v === 'none' ? '' : v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">受け取らない（推奨）</SelectItem>
                    {(mailboxes.data?.items ?? []).map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {save.isError ? <ErrorAlert error={save.error} /> : null}

              <div className="flex justify-end">
                <Button
                  onClick={() => save.mutate(selected === '' ? null : selected)}
                  loading={save.isPending}
                  disabled={!touched}
                >
                  保存する
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------- 削除 */

function DangerSection({
  domainId,
  domainName,
  mailboxCount,
  onDeleted,
}: {
  domainId: string;
  domainName: string;
  mailboxCount: number;
  onDeleted: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const del = useMutation({
    // 削除も 202 + jobId（オフボードのジョブが走る）。
    mutationFn: () => api.del<{ jobId: string }>(`/domains/${encodeURIComponent(domainId)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.domains() });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
      setOpen(false);
      onDeleted();
    },
  });

  return (
    <Card className="border-destructive/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-destructive">ドメインを削除する</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          削除すると、このドメインのメールは受信できなくなります。
          {mailboxCount > 0
            ? `現在このドメインには ${mailboxCount} 件のメールアドレスがあります。先にすべて削除してください。`
            : 'このドメインにメールアドレスはありません。'}
        </p>
        <Button
          variant="destructive"
          disabled={mailboxCount > 0}
          onClick={() => setOpen(true)}
        >
          <Trash2 aria-hidden />
          このドメインを削除する
        </Button>
        {del.isError ? <ErrorAlert error={del.error} /> : null}
      </CardContent>

      <ConfirmTextDialog
        open={open}
        onOpenChange={setOpen}
        title={`${domainName} を削除します`}
        confirmText={domainName}
        consequences={[
          'このドメイン宛のメールは受信できなくなります。',
          'DNS に設定した MX / SPF / DKIM は無効になります。',
          '同じドメインを再登録する場合、DNS の設定をやり直す必要があります。',
        ]}
        pending={del.isPending}
        error={del.error}
        onConfirm={() => del.mutate()}
      />
    </Card>
  );
}
